import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import BlockIcon from "@material-ui/icons/Block";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ScheduleBlockModal from "../../components/ScheduleBlockModal";
import MobileRecordCard, { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
    textAlign: "center",
  },
  chipFullDay: {
    backgroundColor: theme.palette.error.light,
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.72rem",
  },
  timeRange: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
}));

const ScheduleBlocks = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingBlock, setDeletingBlock] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const isAdmin = user.profile === "admin";
  const [providers, setProviders] = useState([]);
  const [providerFilter, setProviderFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(moment().format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(
    moment().add(30, "days").format("YYYY-MM-DD")
  );

  // Carrega lista de profissionais para o filtro
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const { data } = await api.get("/users/list");
        setProviders((data || []).filter((u) => u.isServiceProvider));
      } catch (err) {
        toastError(err);
      }
    };
    fetchProviders();
  }, []);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedule-blocks", {
        params: {
          userId: providerFilter || undefined,
          dateFrom,
          dateTo,
        },
      });
      setBlocks(data.blocks || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [providerFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const handleDelete = async () => {
    if (!deletingBlock) return;
    try {
      await api.delete(`/schedule-blocks/${deletingBlock.id}`);
      toast.success("Bloqueio removido.");
      fetchBlocks();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingBlock(null);
    }
  };

  const formatDate = (dateStr) =>
    moment(dateStr, "YYYY-MM-DD").format("DD/MM/YYYY");

  const formatWeekday = (dateStr) => {
    const date = moment(dateStr, "YYYY-MM-DD").toDate();
    const weekday = date.toLocaleDateString("pt-BR", { weekday: "short" });
    // pt-BR retorna "seg.", "dom."… — remove o ponto e capitaliza a inicial.
    return weekday
      .replace(".", "")
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  const renderPeriod = (block) =>
    block.isFullDay ? (
      <Chip
        icon={<BlockIcon style={{ fontSize: 14 }} />}
        label="Dia inteiro"
        size="small"
        className={classes.chipFullDay}
      />
    ) : (
      <span className={classes.timeRange}>
        <AccessTimeIcon style={{ fontSize: 14 }} />
        {`${block.startTime} – ${block.endTime}`}
      </span>
    );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingBlock
            ? `Remover bloqueio de ${deletingBlock.user?.name || ""}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        Os horários bloqueados voltarão a estar disponíveis para agendamento.
      </ConfirmationModal>

      <ScheduleBlockModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchBlocks}
      />

      <MainHeader>
        <Title>Bloqueios de Agenda</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filterRow}>
            {isAdmin && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                <InputLabel>Profissional</InputLabel>
                <Select
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                  label="Profissional"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="De"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
            />

            <TextField
              label="Até"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setModalOpen(true)}
              startIcon={<EventBusyIcon />}
            >
              Adicionar Bloqueio
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Estado vazio (compartilhado por mobile e desktop) */}
      {!loading && blocks.length === 0 && (
        <Paper className={classes.mainPaper} variant="outlined">
          <Box className={classes.emptyState}>
            <EventBusyIcon style={{ fontSize: 48, marginBottom: 12 }} />
            <Typography variant="body1">
              Nenhum bloqueio cadastrado para este período.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Clique em "Adicionar Bloqueio" para registrar uma
              indisponibilidade.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Mobile: cards */}
      {isMobile && (blocks.length > 0 || loading) && (
        <Paper className={classes.mainPaper} variant="outlined">
          {blocks.map((block) => (
            <MobileRecordCard
              key={block.id}
              title={`${formatDate(block.date)} · ${formatWeekday(block.date)}`}
              subtitle={block.user?.name || "—"}
              headerAction={
                <Tooltip title="Remover bloqueio">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeletingBlock(block);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              rows={[
                { label: "Período", value: renderPeriod(block) },
                { label: "Motivo", value: block.reason || "—" },
              ]}
            />
          ))}
          {loading &&
            [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
        </Paper>
      )}

      {/* Desktop: tabela */}
      {!isMobile && (blocks.length > 0 || loading) && (
        <Paper className={classes.mainPaper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Profissional</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell align="center" style={{ width: 60 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blocks.map((block) => (
                <TableRow key={block.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {formatDate(block.date)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatWeekday(block.date)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>{block.user?.name || "—"}</TableCell>

                  <TableCell>{renderPeriod(block)}</TableCell>

                  <TableCell>
                    {block.reason ? (
                      block.reason
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Tooltip title="Remover bloqueio">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingBlock(block);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRowSkeleton columns={5} />}
            </TableBody>
          </Table>
        </Paper>
      )}
    </MainContainer>
  );
};

export default ScheduleBlocks;
